/**
 * Wording of the lock-screen timer notifications (booked when the practice
 * timer starts; iOS shows them with the app asleep). Pure, so it's tested here
 * and the app only schedules what this returns.
 *
 *   Period change   title  "Indy · until 4:05"
 *                   body   "OL: Sleds" / "QB/RB: Mesh"        (one line per group, split periods only)
 *                          "Next 4:05: Team O"                (or "Next 4:05: Indy — OL: Chutes · RB: Ball security")
 *   30-second       title  "30 sec · Indy ends"
 *                   body   "Next: Team O until 4:25"
 *                          "OL: Chutes" / "RB: Ball security" (one line per group, split periods only)
 *
 * Coaches split across groups each find their own line. Times are 12-hour
 * without am/pm, matching the lock-screen clock beside them.
 */
import type { Assignment, Period, PlanData } from '../types';
import { minToTime, timeToMin } from './math';

export interface CueActivity {
  /** Group chips joined with "/" ("QB/RB"); '' when the assignment has none. */
  who: string;
  what: string;
}
export interface CuePeriod {
  /** One activity → its name; split period → the period type's label. */
  label: string;
  /** Filled only when the period is split into 2+ assignments. */
  acts: CueActivity[];
}
export interface CueNote {
  /** Stable per plan slot: 2·i for the 30-second note before period i, 2·i+1 for its start. */
  slot: number;
  atMs: number;
  title: string;
  body: string;
}

/** "15:05" → "3:05", "12:30" → "12:30", "00:10" → "12:10". */
export function clock12(hhmm: string): string {
  const m = timeToMin(hhmm);
  const h = Math.floor(m / 60) % 12 || 12;
  return `${h}:${String(m % 60).padStart(2, '0')}`;
}

export function describePeriod(p: Period, typeLabel: string, drillName: (id: string) => string | undefined): CuePeriod {
  const name = (a: Assignment) => a.customActivity.trim() || (a.drillId ? drillName(a.drillId) : '') || typeLabel;
  const as = p.assignments ?? [];
  if (as.length <= 1) return { label: as[0] ? name(as[0]) : typeLabel, acts: [] };
  return { label: typeLabel, acts: as.map((a) => ({ who: (a.groups ?? []).join('/'), what: name(a) })) };
}

const actLine = (a: CueActivity) => (a.who ? `${a.who}: ${a.what}` : a.what);

/** Wall-clock end of period i as "H:MM", or null for an open-ended last period. */
function endOf(plan: PlanData, i: number): string | null {
  const ps = plan.periods;
  if (i < ps.length - 1) return clock12(ps[i + 1]!.time);
  const d = ps[i]?.duration;
  return d ? clock12(minToTime(timeToMin(ps[i]!.time) + d)) : null;
}

/**
 * Every cue note for the plan, from `anchorMs` (period 0's wall-clock start).
 * Period 0 gets none — the coach just pressed Start. Callers drop past ones.
 */
export function cueNotes(plan: PlanData, anchorMs: number, describe: (i: number) => CuePeriod): CueNote[] {
  const ps = plan.periods;
  if (!ps.length) return [];
  const first = timeToMin(ps[0]!.time);
  const out: CueNote[] = [];
  for (let i = 1; i < ps.length; i++) {
    const at = anchorMs + (timeToMin(ps[i]!.time) - first) * 60000;
    const cur = describe(i);
    const prev = describe(i - 1);
    const end = endOf(plan, i);
    const untilTxt = end ? ` until ${end}` : ' · last period';

    out.push({
      slot: i * 2,
      atMs: at - 30_000,
      title: `30 sec · ${prev.label} ends`,
      body: [`Next: ${cur.label}${untilTxt}`, ...cur.acts.map(actLine)].join('\n'),
    });

    const lines = cur.acts.map(actLine);
    if (i < ps.length - 1) {
      const nx = describe(i + 1);
      const nxt = `Next ${clock12(ps[i + 1]!.time)}: ${nx.label}`;
      lines.push(nx.acts.length ? `${nxt} — ${nx.acts.map(actLine).join(' · ')}` : nxt);
    } else {
      lines.push('Last period');
    }
    out.push({
      slot: i * 2 + 1,
      atMs: at,
      title: end ? `${cur.label} · until ${end}` : cur.label,
      body: lines.join('\n'),
    });
  }
  return out;
}
